<template>
  <div class="search-bar" ref="searchBarRef">
    <div class="search-input-wrapper">
      <el-input
        v-model="searchQuery"
        placeholder="搜索科学概念..."
        :prefix-icon="Search"
        clearable
        @input="handleInput"
        @keyup.enter="handleSearch"
        @focus="showSuggestions = true"
        class="search-input"
      />
      <el-button 
        type="primary" 
        :icon="Search"
        @click="handleSearch"
        class="search-button"
      >
        搜索
      </el-button>
    </div>

    <!-- 搜索建议下拉列表 -->
    <div 
      v-if="showSuggestions && (suggestions.length > 0 || searchHistory.length > 0)"
      class="suggestions-dropdown"
    >
      <!-- 搜索历史 -->
      <div v-if="searchHistory.length > 0 && !searchQuery" class="suggestion-section">
        <div class="suggestion-header">
          <span class="suggestion-title">
            <el-icon><Clock /></el-icon>
            搜索历史
          </span>
          <el-button 
            text 
            size="small"
            @click="clearHistory"
          >
            清除
          </el-button>
        </div>
        <div 
          v-for="(item, index) in searchHistory" 
          :key="`history-${index}`"
          class="suggestion-item"
          @click="selectSuggestion(item)"
        >
          <el-icon><Clock /></el-icon>
          <span>{{ item }}</span>
        </div>
      </div>

      <!-- 搜索建议 -->
      <div v-if="suggestions.length > 0" class="suggestion-section">
        <div class="suggestion-header">
          <span class="suggestion-title">
            <el-icon><Search /></el-icon>
            搜索建议
          </span>
        </div>
        <div 
          v-for="suggestion in suggestions" 
          :key="suggestion.id"
          class="suggestion-item"
          @click="selectSuggestion(suggestion.title)"
        >
          <el-icon><Document /></el-icon>
          <div class="suggestion-content">
            <span class="suggestion-text">{{ suggestion.title }}</span>
            <el-tag size="small" class="suggestion-tag">{{ suggestion.category }}</el-tag>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { Search, Clock, Document } from '@element-plus/icons-vue'
import { ElInput, ElButton, ElIcon, ElTag } from 'element-plus'
// Import Element Plus CSS
import 'element-plus/dist/index.css'

const props = defineProps({
  suggestions: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['search', 'input'])

const searchQuery = ref('')
const showSuggestions = ref(false)
const searchHistory = ref([])
const searchBarRef = ref(null)

// 防抖定时器
let debounceTimer = null

/**
 * 处理输入事件（带防抖）
 */
function handleInput(value) {
  clearTimeout(debounceTimer)
  
  debounceTimer = setTimeout(() => {
    emit('input', value)
  }, 500) // 500ms 防抖
}

/**
 * 处理搜索事件
 */
function handleSearch() {
  const query = searchQuery.value.trim()
  
  if (query) {
    // 添加到搜索历史
    addToHistory(query)
    
    // 触发搜索事件
    emit('search', query)
    
    // 隐藏建议
    showSuggestions.value = false
  }
}

/**
 * 选择建议项
 */
function selectSuggestion(text) {
  searchQuery.value = text
  showSuggestions.value = false
  handleSearch()
}

/**
 * 添加到搜索历史
 */
function addToHistory(query) {
  // 移除重复项
  const filtered = searchHistory.value.filter(item => item !== query)
  
  // 添加到开头
  searchHistory.value = [query, ...filtered].slice(0, 10) // 最多保存 10 条
  
  // 保存到 LocalStorage
  saveHistory()
}

/**
 * 清除搜索历史
 */
function clearHistory() {
  searchHistory.value = []
  localStorage.removeItem('search-history')
}

/**
 * 从 LocalStorage 加载历史
 */
function loadHistory() {
  try {
    const stored = localStorage.getItem('search-history')
    if (stored) {
      searchHistory.value = JSON.parse(stored)
    }
  } catch (error) {
    console.error('Failed to load search history:', error)
  }
}

/**
 * 保存历史到 LocalStorage
 */
function saveHistory() {
  try {
    localStorage.setItem('search-history', JSON.stringify(searchHistory.value))
  } catch (error) {
    console.error('Failed to save search history:', error)
  }
}

/**
 * 点击外部关闭建议
 */
function handleClickOutside(event) {
  if (searchBarRef.value && !searchBarRef.value.contains(event.target)) {
    showSuggestions.value = false
  }
}

onMounted(() => {
  loadHistory()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  clearTimeout(debounceTimer)
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.search-bar {
  position: relative;
  width: 100%;
  max-width: 600px;
}

.search-input-wrapper {
  display: flex;
  gap: 8px;
}

.search-input {
  flex: 1;
}

.search-button {
  flex-shrink: 0;
}

.suggestions-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
}

.suggestion-section {
  padding: 8px 0;
}

.suggestion-section + .suggestion-section {
  border-top: 1px solid #f0f0f0;
}

.suggestion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  color: #909399;
  font-size: 12px;
}

.suggestion-title {
  display: flex;
  align-items: center;
  gap: 4px;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.suggestion-item:hover {
  background-color: #f5f7fa;
}

.suggestion-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: 8px;
}

.suggestion-text {
  flex: 1;
  color: #303133;
}

.suggestion-tag {
  flex-shrink: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-bar {
    max-width: 100%;
  }
  
  .search-input-wrapper {
    gap: 0.5rem;
  }
  
  .search-button {
    min-width: 80px;
    min-height: 44px;
    padding: 0 1.25rem;
  }

  .suggestions-dropdown {
    max-height: 60vh;
  }

  .suggestion-item {
    padding: 0.875rem 1rem;
    min-height: 48px;
    -webkit-tap-highlight-color: transparent;
  }

  .suggestion-header {
    padding: 0.625rem 1rem;
  }
}

@media (max-width: 480px) {
  .search-input-wrapper {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .search-button {
    width: 100%;
    min-height: 44px;
  }

  .suggestions-dropdown {
    max-height: 50vh;
  }

  .suggestion-item {
    padding: 0.75rem 0.875rem;
    font-size: 0.9375rem;
  }
}
</style>
